import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, ToggleField, SecretPasswordField } from '../../../components';
import { Field, useFormikContext } from 'formik';
import { ClientArea } from '../../../services';

interface ClientAreaFormProps {};

function ClientAreaForm(props: ClientAreaFormProps) {
    const { t } = useTranslation();
    const { values } = useFormikContext<AnyObj>();
    const enabled = !!values?.client_area?.enabled;

    const [syncing, setSyncing] = useState(false);
    const [syncStatus, setSyncStatus] = useState<'idle' | 'succeeded' | 'failed'>('idle');
    const [syncError, setSyncError] = useState<string | null>(null);

    const syncClients = async () => {
        if (!enabled || syncing)
            return;

        setSyncing(true);
        setSyncStatus('idle');
        setSyncError(null);
        try {
            await ClientArea.syncClients();
            setSyncStatus('succeeded');
        } catch (err: any) {
            const code = err?.message || 'sync_failed';
            setSyncError(t(`settings.client_area_sync_error_${code}`, {
                defaultValue: t('settings.client_area_sync_failed') || ''
            }) || null);
            setSyncStatus('failed');
        } finally {
            setSyncing(false);
        }
    };

    return (
        <div className="flex flex-col lg:flex-row space-y-6 lg:space-y-0 space-x-0 lg:space-x-12">
            <div className="flex-1">
                <h2 className="mt-4 mb-1 text-base font-semibold leadin text-lg text-gray-900 dark:text-gray-100">
                    {t('settings.client_area')}
                </h2>
                <div className="mt-5">
                    <div className="mt-2 flex space-between">
                        <label htmlFor="enable" className="flex-1 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                            {t('settings.enable_client_area')}
                        </label>
                        <ToggleField name="client_area.enabled" className="inline"/>
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="api_url" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.client_area_api_url')}
                    </label>
                    <div className="mt-2">
                        <Field
                            type="text"
                            name="client_area.api_url"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                            placeholder="https://tickets.net/showTicket?id=%d"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label htmlFor="api_key" className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.client_area_api_key')}
                    </label>
                    <div className="mt-2">
                        <SecretPasswordField
                            name="client_area.api_key"
                            hasFlagName="client_area.hasApiKey"
                        />
                    </div>
                </div>
                <div className="mt-5">
                    <label className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">
                        {t('settings.client_area_sync')}
                    </label>
                    <small className="text-gray-500 dark:text-gray-400">{t('settings.client_area_sync_desc')}</small>
                    <div className="mt-2">
                        <Button
                            type="button"
                            title={t('settings.client_area_sync_button') || ''}
                            color="secondary"
                            icon="sync"
                            loading={syncing}
                            onClick={syncClients}
                            className={!enabled || syncing ? 'opacity-50 pointer-events-none' : ''}
                        />
                    </div>
                    {syncStatus === 'succeeded' && (
                        <div className="mt-3">
                            <Alert title={t('settings.client_area_sync_succeeded') || ''} color="success" icon="check" />
                        </div>
                    )}
                    {syncStatus === 'failed' && (
                        <div className="mt-3">
                            <Alert title={syncError || t('settings.client_area_sync_failed') || ''} color="failure" icon="warning" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ClientAreaForm;
